import {
  Body,
  Button,
  Icon,
  InstallableContainer,
  Medium,
  Padding,
  Spacer,
  useAppVersion,
} from "@vestaboard/installables";

import { ButtonBase, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { BlendedMarkIcon } from "./BlendedMarkIcon";
import { BLENDED_ICON_BACKGROUND } from "./BlendedMarkIcon.paths";
import { PLATFORM_ENDPOINT } from "../constants";
import { IChannel, ISubscription } from "../types";
import { makeStyles } from "@mui/styles";
import { useGetQueryString } from "../hooks/useGetQueryString";
import { useFeatureFlagEnabled } from "../hooks/useFeatureFlagEnabled";
import { useRemoveChannel } from "../hooks/useRemoveChannel";
import { ReactNode, useMemo, useState } from "react";

const BLENDABLE_CHANNELS_ID = "8a9f62f2-af93-4c8e-b0c3-bc21225cd0d0";

interface IMessageSets {
  subscription: ISubscription;
  updateSubscription: (subscription: ISubscription) => void;
  platform: string;
  setTab: (tab: number) => void;
}

const useStyles = makeStyles({
  iconAndTextContainer: {
    display: "flex",
  },
  bold: {
    color: "#DDD",
  },
  icon: {
    height: 16,
    width: 16,
  },
  messageSetContainer: {
    background: "#393a3e",
    borderRadius: 12,
  },
  messageSet: {
    display: "flex",
    color: "#FFF",
    borderBottom: "1px solid #4a4c50",
    padding: 10,
    paddingRight: 24,
    paddingLeft: 24,
    alignItems: "center",
    justifyContent: "space-between",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  messageSetLeft: {
    display: "flex",
    alignItems: "center",
  },
  messageSetIcon: {
    height: 40,
    width: 40,
    marginRight: 32,
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
    borderRadius: 7,
    objectFit: "cover",
    flexShrink: 0,
  },
  messageSetIconPlaceholder: {
    height: 40,
    width: 40,
    marginRight: 32,
    background: "#4a4c50",
    borderRadius: 7,
    flexShrink: 0,
  },
  statusIcons: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  needsSetup: {
    display: "flex",
    alignItems: "center",
    color: "#F5A623",
    cursor: "pointer",
  },
  needsSetupIcon: {
    fontSize: 22,
  },
  muted: {
    display: "flex",
    alignItems: "center",
    color: "#9AA0A6",
    cursor: "pointer",
  },
  mutedIcon: {
    fontSize: 22,
  },
  info: {
    display: "flex",
    alignItems: "center",
    color: "#9AA0A6",
    cursor: "pointer",
  },
  infoIcon: {
    fontSize: 20,
  },
  updateCta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: BLENDED_ICON_BACKGROUND,
    borderRadius: 8,
    padding: 12,
  },
  updateCtaIcon: {
    fontSize: 40,
    flexShrink: 0,
    display: "block",
  },
  updateCtaCopy: {
    color: "#F4F5F8",
    fontSize: 14,
    lineHeight: "20px",
    minWidth: 0,
    flex: 1,
  },
  discoverLink: {
    textDecoration: "none",
  },
  bottomButtons: {
    position: "fixed",
    bottom: 0,
    width: "100vw",
    left: 0,
    background: "rgba(36, 40, 41, 0.9)",
  },
});

const StatusTooltip = ({
  title,
  className,
  children,
}: {
  title: string;
  className: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip
      title={title}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      arrow
      placement="left"
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      describeChild
      PopperProps={{
        style: { zIndex: 3000 },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "#000",
            color: "#FFF",
          },
        },
        arrow: {
          sx: {
            color: "#000",
          },
        },
      }}
    >
      <span
        className={className}
        tabIndex={0}
        role="button"
        onClick={() => setOpen(true)}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export const MessageSets = (props: IMessageSets) => {
  const classes = useStyles();
  const { greaterThanOrEqualTo } = useAppVersion();
  const [deleted, setDeleted] = useState<string[]>([]);
  const queryString = useGetQueryString();
  const removeChannel = useRemoveChannel({
    subscriptionId: props.subscription.id,
    subscriptionConfigurationToken: queryString.subscriptionConfigurationToken,
  });

  const messageSets: IChannel[] = useMemo(() => {
    return props.subscription.channels
      .filter(({ id }) => !deleted.includes(id))
      .slice()
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [deleted, props.subscription.channels]);

  const hasSubscriptionBlends = messageSets.some(
    ({ type }) => type === "subscription"
  );
  const showUpdateBanner =
    useFeatureFlagEnabled("blended-and-schedule") && hasSubscriptionBlends;

  return (
    <>
      <Padding>
        <Spacer size="medium" />
        <Medium>
          Blend a variety of topics you like and have Vestaboard surprise you on
          a frequency of your choosing.
        </Medium>
        <Spacer size="medium" />
        <div className={classes.iconAndTextContainer}>
          <Medium>
            To add more content to your Blended channel, click{" "}
            <strong className={classes.bold}>Blend</strong> while discovering
            other channels within Vestaboard+.
          </Medium>
        </div>
        {showUpdateBanner ? (
          <>
            <Spacer size="large" />
            <div className={classes.updateCta}>
              <BlendedMarkIcon
                markColor="#F4F5F8"
                className={classes.updateCtaIcon}
              />
              <span className={classes.updateCtaCopy}>
                Update the Vestaboard app for the latest Manage experience.
              </span>
            </div>
          </>
        ) : null}
        {showUpdateBanner ? (
          <Spacer size="large" />
        ) : (
          <Spacer size="extraLarge" />
        )}
        <div className={classes.messageSetContainer}>
          {messageSets.map((messageSet) => (
            <div
              key={`${messageSet.type}-${messageSet.id}`}
              className={classes.messageSet}
            >
              <div className={classes.messageSetLeft}>
                {messageSet.icon ? (
                  <img
                    src={messageSet.icon}
                    alt={messageSet.title || ""}
                    className={classes.messageSetIcon}
                  />
                ) : (
                  <div className={classes.messageSetIconPlaceholder} />
                )}
                <Body>{messageSet.title}</Body>
              </div>
              {messageSet.type === "subscription" ? (
                <div className={classes.statusIcons}>
                  {messageSet.muted ? (
                    <StatusTooltip
                      title="Muted. Update settings from the Manage screen."
                      className={classes.muted}
                    >
                      <VolumeOffIcon className={classes.mutedIcon} />
                    </StatusTooltip>
                  ) : messageSet.needsConfiguration ? (
                    <StatusTooltip
                      title="Needs setup. Update settings from the Manage screen."
                      className={classes.needsSetup}
                    >
                      <WarningAmberIcon className={classes.needsSetupIcon} />
                    </StatusTooltip>
                  ) : (
                    <StatusTooltip
                      title="Update settings from the Manage screen."
                      className={classes.info}
                    >
                      <InfoOutlinedIcon className={classes.infoIcon} />
                    </StatusTooltip>
                  )}
                </div>
              ) : (
                <ButtonBase
                  onClick={async () => {
                    setDeleted((current) => [...current, messageSet.id]);
                    await removeChannel(messageSet.id);
                  }}
                >
                  <Icon type="delete" />
                </ButtonBase>
              )}
            </div>
          ))}
        </div>
        <Spacer size="extraLarge" />
        <Spacer size="extraLarge" />
        {props.subscription.setupComplete ? null : (
          <>
            <Spacer size="extraLarge" />
            <Spacer size="extraLarge" />
          </>
        )}
      </Padding>
      <div className={classes.bottomButtons}>
        <InstallableContainer>
          <Padding>
            <a
              href={
                greaterThanOrEqualTo("1.26.0")
                  ? `${PLATFORM_ENDPOINT}/deeplink/plus/feature-types/${BLENDABLE_CHANNELS_ID}`
                  : `${PLATFORM_ENDPOINT}/deeplink/plus/discover?platform=${props.platform}`
              }
              target={props.platform === "web" ? "_parent" : "_blank"}
              rel="noreferrer"
              className={classes.discoverLink}
            >
              <Button onClick={() => {}} buttonType="outline">
                Discover More Channels
              </Button>
            </a>
            {props.subscription.setupComplete ? null : (
              <>
                <Spacer size="medium" />
                <Button
                  onClick={() => {
                    props.setTab(1);
                  }}
                  buttonType="white"
                >
                  Complete Setup
                </Button>
              </>
            )}
          </Padding>
        </InstallableContainer>
      </div>
    </>
  );
};
